import type { ContractStatus, ExternalContractDetail } from "@closer/shared";
import {
  CONTRACT_DISPLAY_PHASE_NAMES,
  CONTRACT_PARTY_ENTITY_TYPE_NAMES,
  CONTRACT_PARTY_ENTITY_TYPE_UNKNOWN,
  CONTRACT_PARTY_POSITION_NAMES,
  CONTRACT_STATUS_NAMES,
  contractDisplayPhase,
} from "@closer/shared";
import { Badge, Card, EmptyState, PageHeader, Skeleton } from "@closer/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router";

import { ClauseReadOnlyList } from "../../components/ClauseReadOnlyList";
import { api, ApiRequestError } from "../../lib/apiClient";

/**
 * Haricî sözleşme görünümü (CLOS-147 `[128]`) — başlık, durum, versiyon METADATA'sı.
 *
 * ⚠ Aksiyonlar `allowedActions` listesinden ÇİZİLİR ve liste bugün sunucudan BOŞ gelir:
 * haricî yorum/revizyon akışları `[137]`+ kartlarının işi. Burada buton uydurmak, "izin
 * verilmeyen butonlar yalnız gizlenmez" kuralının tersinden ihlali olurdu — sunucunun
 * vermediği aksiyonu arayüz icat etmez.
 *
 * ⚠ Erişim kaybı = 404: sunucu her istekte kararı yeniden veriyor; ekran bu durumda
 * kullanıcıyı bilgilendirip listeye yönlendiriyor (kartın "erişim kaybı durumu").
 */
export function ExternalContractDetailPage() {
  const { id = "" } = useParams();

  const detay = useQuery({
    queryKey: ["external", "contract", id],
    queryFn: async () => (await api<ExternalContractDetail>(`/api/external/contracts/${id}`)).data,
    retry: false,
  });

  if (detay.isPending) {
    return (
      <div role="status" aria-label="Sözleşme yükleniyor">
        <Skeleton lines={5} />
      </div>
    );
  }

  if (detay.isError) {
    const erisimYok = detay.error instanceof ApiRequestError && detay.error.status === 404;
    return (
      <div className="space-y-4">
        <EmptyState
          title={erisimYok ? "Bu sözleşmeye erişiminiz yok" : "Sözleşme yüklenemedi"}
          description={
            erisimYok
              ? "Erişiminiz sona ermiş ya da kaldırılmış olabilir. Yeni bir davet için sizi davet eden kişiyle iletişime geçin."
              : "Lütfen sayfayı yenileyin; sorun sürerse sizi davet eden kişiyle iletişime geçin."
          }
        />
        <p className="text-center">
          <Link to="/harici" className="font-medium text-primary hover:underline">
            Erişimlerime dön
          </Link>
        </p>
      </div>
    );
  }

  const s = detay.data;
  /**
   * ⚠ Tek merkezî eşleme; ekran kendi yorumunu yapmıyor. Bilinmeyen durumda evre
   * gösterilmiyor (uydurulmuyor), yalnız kanonik durum adı kalıyor.
   */
  const evre = contractDisplayPhase(s.status);
  /**
   * ⚠ BİLİNMEYEN DURUMDA GÜVENLİ GENEL GÖRÜNÜM (Shared Detail §8.2): yanlış evre tahmin
   * edilmiyor ama boş rozet ya da kayıp alan da bırakılmıyor. Sıra: kanonik evre → kanonik
   * durum adı → nötr "Durum bilinmiyor". Ekran hiçbir hâlde çökmüyor.
   */
  const evreAdi =
    (evre ? CONTRACT_DISPLAY_PHASE_NAMES[evre] : null) ??
    CONTRACT_STATUS_NAMES[s.status as ContractStatus] ??
    "Durum bilinmiyor";
  return (
    <div className="w-full space-y-5">
      <p>
        <Link to="/harici" className="text-sm font-medium text-primary hover:underline">
          ← Erişimlerim
        </Link>
      </p>

      <PageHeader
        title={s.title}
        description={s.organizationName}
        meta={
          <>
            {/*
              ⚠ DURUM SUNUCUDAN GELİYORDU AMA HİÇ GÖSTERİLMİYORDU (Shared Detail §6).
              `status` alanı `/api/external` yanıtında zaten vardı; ekran onu kullanmıyordu
              ve misafir sözleşmenin hangi aşamada olduğunu göremiyordu. Etiket kanonik
              durumdan, tahminden değil.
            */}
            <Badge tone={evre ? "info" : "neutral"}>{evreAdi}</Badge>
            <Badge>Görüntüleme</Badge>
            {s.endsAt ? (
              <Badge tone="warning">
                {new Date(s.endsAt).toLocaleDateString("tr-TR")} tarihine kadar
              </Badge>
            ) : null}
          </>
        }
      />

      {/*
        MİSAFİRİN GÖRDÜĞÜ BELGE (CLOS-1127 · K2): kanonik metin ya da sürümden çıkarılmış metin,
        salt-okunur ve paylaşılan sözleşme önizlemesiyle aynı çiziciyle. İndirme, düzenleme, yorum
        yok — sunucu bunları yükte taşımaz, arayüz icat etmez.
      */}
      {/*
        ⚠ MİSAFİR HUKUKİ TARAFLARI GÖRÜR (Shared Detail §7). VIEW-only olmak, sözleşmenin
        taraflarının kim olduğunu görmeye engel değil: hukuki taraf sözleşme bağlamıdır,
        organizasyonun iç ACL verisi değil. Sunucu yalnız ad, tür ve saf gönderiyor.
      */}
      <Card title="Taraflar">
        {s.parties.length === 0 ? (
          <p data-testid="harici-taraf-yok" className="text-sm text-fg-muted">
            Bu sözleşmede henüz hukuki taraf tanımlanmadı.
          </p>
        ) : (
          <ul className="flex flex-col gap-2" data-testid="harici-taraflar">
            {s.parties.map((taraf, i) => (
              <li key={`${taraf.displayName}-${i}`} className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-fg">{taraf.displayName}</span>
                {/*
                  ⚠ ETİKET SÖZLÜKTEN, EKRANDAN DEĞİL (BUG-013 · Mega E2E'de ölçüldü).

                  Burada üç metin elle yazılıydı ve biri ürünün geri kalanıyla ÇELİŞİYORDU:
                  kuruluş için "Kurum" deniyordu, kanonik sözlük ise "Kuruluş" diyor
                  (`CONTRACT_PARTY_ENTITY_TYPE_NAMES`; kart etiketi testi bunu
                  "Karşı taraf · Kuruluş" diye kilitliyor). Aynı taraf, misafir yüzeyinde
                  farklı bir sözcükle görünüyordu — kullanıcı iki ekranda iki farklı şey
                  okuyup hangisinin doğru olduğunu bilemiyordu.

                  ⚠ Depo kuralı (CLAUDE.md refleks 4): kullanıcıya görünen metin şemada
                  tanımlanır, ekran string uydurmaz. Sözlük yarın değişirse bu ekran da
                  onunla değişir.
                */}
                <span className="text-sm text-fg-muted">
                  {taraf.entityType
                    ? CONTRACT_PARTY_ENTITY_TYPE_NAMES[taraf.entityType]
                    : CONTRACT_PARTY_ENTITY_TYPE_UNKNOWN}
                </span>
                {taraf.side ? (
                  <Badge tone="neutral">{CONTRACT_PARTY_POSITION_NAMES[taraf.side]}</Badge>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Card
        title={
          s.document.mode === "version" && s.document.versionNumber !== null
            ? `Sözleşme metni — Versiyon ${s.document.versionNumber}`
            : "Sözleşme metni"
        }
      >
        {s.document.document && s.document.document.clauses.length > 0 ? (
          <ClauseReadOnlyList maddeler={s.document.document.clauses} testId="misafir-maddeler" />
        ) : (
          <p className="text-sm text-fg-muted" data-testid="misafir-metin-yok">
            {s.document.note ?? "Bu sözleşmenin henüz metni yok."}
          </p>
        )}
      </Card>
      <Card title="Versiyonlar">
        {s.versions.length === 0 ? (
          <p className="text-sm text-fg-muted">Bu sözleşmenin henüz kayıtlı versiyonu yok.</p>
        ) : (
          <ul className="divide-y divide-border-subtle">
            {s.versions.map((versiyon) => (
              <li key={versiyon.versionNumber} className="flex items-center justify-between py-2">
                <p className="text-sm text-fg">Versiyon {versiyon.versionNumber}</p>
                <p className="text-xs text-fg-soft">
                  {new Date(versiyon.createdAt).toLocaleDateString("tr-TR")}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
